import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';

// Украинский однодатовый пикер с кастомным календарём.
// Повністю самодостатній — не залежить від App.js.
// Props: date (ISO), setDate, min (ISO, опційно), max (ISO, опційно), ariaLabel

const MONTHS_UA = ['Січень','Лютий','Березень','Квітень','Травень','Червень',
                   'Липень','Серпень','Вересень','Жовтень','Листопад','Грудень'];
const WDAYS_UA  = ['Пн','Вт','Ср','Чт','Пт','Сб','Нд'];

function toISO(d) {
  if (!d) return '';
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}
function parseISO(iso) {
  if (!iso) return null;
  const [y,m,d] = iso.split('-').map(Number);
  const dt = new Date(y,m-1,d); dt.setHours(0,0,0,0); return dt;
}
function formatDisplay(iso) {
  if (!iso) return '';
  const [y,m,d] = iso.split('-');
  return `${d}.${m}.${y}`;
}
function parseRaw(raw) {
  if (!raw) return null;
  const s = raw.replace(/[/-]/g,'.');
  const parts = s.split('.');
  if (parts.length === 3) {
    const dd=parseInt(parts[0],10), mm=parseInt(parts[1],10), yy=parseInt(parts[2],10);
    if (dd>=1&&dd<=31&&mm>=1&&mm<=12&&yy>=1900&&yy<=2100) {
      const dt=new Date(yy,mm-1,dd); dt.setHours(0,0,0,0);
      if (dt.getDate()===dd&&dt.getMonth()===mm-1) return dt;
    }
  }
  return null;
}
function daysInMonth(y,m) { return new Date(y,m+1,0).getDate(); }
function firstWeekday(y,m) { const d=new Date(y,m,1).getDay(); return d===0?6:d-1; }

export default function DateSinglePicker({ date, setDate, min, max, ariaLabel = 'Дата' }) {
  const todayObj = new Date(); todayObj.setHours(0,0,0,0);
  const minDate = min ? parseISO(min) : null;
  const maxDate = max ? parseISO(max) : null;

  const [open,       setOpen]       = useState(false);
  const [viewYear,   setViewYear]   = useState(() => new Date().getFullYear());
  const [viewMonth,  setViewMonth]  = useState(() => new Date().getMonth());
  const [yearPicker, setYearPicker] = useState(false);
  const [popupStyle, setPopupStyle] = useState({});
  const [raw,        setRaw]        = useState('');
  const containerRef = useRef(null);
  const portalRef    = useRef(null);

  useEffect(() => { setRaw(formatDisplay(date)); }, [date]);

  const calcPosition = useCallback(() => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const showAbove = (window.innerHeight - rect.bottom) < 360 && rect.top > 360;
    setPopupStyle({
      position:'fixed', zIndex:9999,
      left: rect.left, width: Math.max(rect.width, 230),
      ...(showAbove ? {bottom: window.innerHeight - rect.top + 6} : {top: rect.bottom + 6}),
    });
  }, []);

  useEffect(() => {
    if (!open) return;
    calcPosition();
    window.addEventListener('scroll', calcPosition, true);
    window.addEventListener('resize', calcPosition);
    return () => {
      window.removeEventListener('scroll', calcPosition, true);
      window.removeEventListener('resize', calcPosition);
    };
  }, [open, calcPosition]);

  useEffect(() => {
    if (!open) return;
    const h = e => {
      if (!containerRef.current?.contains(e.target) && !portalRef.current?.contains(e.target)) {
        setOpen(false); setYearPicker(false);
      }
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);

  const prevMonth = () => {
    if (viewMonth===0) { setViewMonth(11); setViewYear(y=>y-1); } else setViewMonth(m=>m-1);
  };
  const nextMonth = () => {
    if (viewMonth===11) { setViewMonth(0); setViewYear(y=>y+1); } else setViewMonth(m=>m+1);
  };

  const handleAdjDayClick = (year, month, day) => {
    const clicked = new Date(year, month, day); clicked.setHours(0,0,0,0);
    if (isOutOfRange(clicked)) return;
    setDate(toISO(clicked));
    setViewYear(year);
    setViewMonth(month);
    setOpen(false);
    setYearPicker(false);
  };

  const openPicker = () => {
    setYearPicker(false);
    const cur = parseISO(date);
    if (cur) { setViewYear(cur.getFullYear()); setViewMonth(cur.getMonth()); }
    else { setViewYear(todayObj.getFullYear()); setViewMonth(todayObj.getMonth()); }
    setOpen(true);
  };

  const isOutOfRange = d => (minDate && d < minDate) || (maxDate && d > maxDate);

  const handleDayClick = day => {
    const clicked = new Date(viewYear, viewMonth, day); clicked.setHours(0,0,0,0);
    if (isOutOfRange(clicked)) return;
    setDate(toISO(clicked));
    setOpen(false);
    setYearPicker(false);
  };

  const handleTodayClick = () => {
    if (isOutOfRange(todayObj)) return;
    setDate(toISO(todayObj));
    setOpen(false);
    setYearPicker(false);
  };

  const handleBlur = () => {
    const dt = parseRaw(raw);
    if (dt) {
      if (isOutOfRange(dt)) { setRaw(formatDisplay(date)); return; }
      const iso = toISO(dt);
      setDate(iso);
      setRaw(formatDisplay(iso));
    } else if (!raw.trim()) {
      setDate('');
      setRaw('');
    } else {
      setRaw(formatDisplay(date));
    }
  };

  const handleKey = e => {
    if (e.key === 'Enter') e.target.blur();
    if (e.key === 'Escape') { setRaw(formatDisplay(date)); e.target.blur(); }
  };

  const totalDays    = daysInMonth(viewYear, viewMonth);
  const leadingEmpty = firstWeekday(viewYear, viewMonth);

  const getDayClasses = day => {
    const d = new Date(viewYear, viewMonth, day); d.setHours(0,0,0,0);
    const iso = toISO(d);
    const cls = ['drp-day'];
    if (iso === date) cls.push('drp-day--selected');
    if (iso === toISO(todayObj)) cls.push('drp-day--today');
    return cls.join(' ');
  };

  const yearBase  = Math.floor(viewYear/12)*12;
  const yearRange = Array.from({length:12},(_,i)=>yearBase+i);

  // Кнопка дня із сусіднього місяця (до/після поточного) — та сама розмітка
  // й логіка класів/disabled, що раніше дублювалась окремо для leading і trailing блоків.
  const renderAdjDay = (y, m, day, key) => {
    const d = new Date(y, m, day); d.setHours(0,0,0,0);
    const iso = toISO(d);
    const disabled = isOutOfRange(d);
    const cls = ['drp-day','drp-day--adj', iso===date?'drp-day--selected':'', iso===toISO(todayObj)?'drp-day--today':''].filter(Boolean).join(' ');
    return <button key={key} type="button" className={cls} onClick={()=>handleAdjDayClick(y,m,day)} disabled={disabled}>{day}</button>;
  };

  const popupEl = open ? createPortal(
    <div ref={portalRef} style={popupStyle}>
      <div className="drp-calendar drp-calendar--open">

        <div className="drp-nav">
          <button type="button" className="drp-nav-btn"
            onClick={yearPicker ? ()=>setViewYear(y=>y-12) : prevMonth}
            aria-label={yearPicker ? 'Попередні роки' : 'Попередній місяць'}>
            <i className="fa-solid fa-chevron-left" />
          </button>
          <button type="button" className="drp-nav-title drp-nav-title--btn"
            onClick={()=>setYearPicker(v=>!v)}
            title={yearPicker ? 'Назад до місяця' : 'Вибрати рік'}>
            {yearPicker
              ? <><i className="bi bi-calendar3 me-1" style={{fontSize:'10px'}}/>{yearRange[0]}–{yearRange[11]}</>
              : <>{MONTHS_UA[viewMonth]}&nbsp;{viewYear}</>}
            <i className={`fa-solid fa-chevron-${yearPicker?'up':'down'} ms-1`} style={{fontSize:'9px'}} />
          </button>
          <button type="button" className="drp-nav-btn"
            onClick={yearPicker ? ()=>setViewYear(y=>y+12) : nextMonth}
            aria-label={yearPicker ? 'Наступні роки' : 'Наступний місяць'}>
            <i className="fa-solid fa-chevron-right" />
          </button>
        </div>

        {yearPicker && (
          <div className="drp-year-grid">
            {yearRange.map(y=>(
              <button key={y} type="button"
                className={`drp-year-btn${y===viewYear?' drp-year-btn--active':''}${y===todayObj.getFullYear()?' drp-year-btn--today':''}`}
                onClick={()=>{setViewYear(y);setYearPicker(false);}}>
                {y}
              </button>
            ))}
          </div>
        )}

        {!yearPicker && (
          <div className="drp-grid">
            {WDAYS_UA.map(d=><div key={d} className="drp-wday">{d}</div>)}
            {Array.from({length:leadingEmpty},(_,i)=>{
              const prevY=viewMonth===0?viewYear-1:viewYear;
              const prevM=viewMonth===0?11:viewMonth-1;
              const day=daysInMonth(prevY,prevM)-leadingEmpty+i+1;
              return renderAdjDay(prevY, prevM, day, `p${i}`);
            })}
            {Array.from({length:totalDays},(_,i)=>{
              const day=i+1;
              return <button key={day} type="button" className={getDayClasses(day)} onClick={()=>handleDayClick(day)}>{day}</button>;
            })}
            {(()=>{
              const trail=(7-((leadingEmpty+totalDays)%7))%7;
              const nextY=viewMonth===11?viewYear+1:viewYear;
              const nextM=viewMonth===11?0:viewMonth+1;
              return Array.from({length:trail},(_,i)=>renderAdjDay(nextY, nextM, i+1, `n${i}`));
            })()}
          </div>
        )}

        <div className="drp-footer">
          <button type="button" className="drp-footer-btn drp-footer-btn--today" onClick={handleTodayClick}>Сьогодні</button>
          <button type="button" className="drp-footer-btn drp-footer-btn--done"
            onClick={()=>{setOpen(false);setYearPicker(false);}}>Закрити</button>
        </div>
      </div>
    </div>,
    document.body
  ) : null;

  return (
    <div className="drp-wrap" ref={containerRef}>
      <div className="drp-inputs-row">
        <div className={`drp-input-group${date?' drp-input-group--filled':''}`}>
          <input type="text" className="drp-text-input" placeholder="дд.мм.рррр"
            value={raw} onChange={e=>setRaw(e.target.value)}
            onFocus={()=>{ if(!open) openPicker(); }}
            onBlur={handleBlur} onKeyDown={handleKey}
            aria-label={ariaLabel} maxLength={10} />
          <button type="button" className="drp-cal-btn"
            onMouseDown={e=>{e.preventDefault(); openPicker();}}
            tabIndex={-1} aria-label="Відкрити календар">
            <i className="fa-regular fa-calendar" aria-hidden="true" />
          </button>
        </div>
      </div>
      {popupEl}
    </div>
  );
}
